import * as React from 'react';

import {
    ADGroups,
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorUsageTypesEnum,
    AssertionDescriptorValueDisplayTypes,
    AssertionDescriptorValueSourceTypeEnum,
    AssertionDescriptors,
    CaseFileStaticItems,
    CustomLogicAssertionDescriptors,
    DisplayItemTypeEnum,
    EntityTypes,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    UserRightsEnum,
} from '../../../utilities/Constants';
import { ICaseAssertionModel, ICaseModel, IDepositionModel, IDocumentModel } from '../../../interfaces/ICase';
import {
    ICaseExpertModel,
    ICaseExpertViewDetailModel,
    IViewCaseExpertModelWithDefaultEdit,
} from '../../../interfaces/ICaseExpert';
import {
    ILookupModel,
    IMultiSelectOption,
    IStaticItemLookupModel,
} from '../../../interfaces/ILookup';
import { cloneDeep, isEmpty } from 'lodash';
import { showErrorToast, showSuccessToast } from '../../../shared/ToastUtils';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Authorization from '../../../stores/Authorization';
import CaseDataCustomLogicHelper from '../../../utilities/CaseDataCustomLogicHelper';
import { CaseEasyUpdateFieldsTableRow } from './CaseEasyUpdateFieldsTableRow';
import { CaseEasyUpdateHeader } from './CaseEasyUpdateHeader';
import { CaseExpertEditor } from '../static-sections/case-experts/CaseExpertEditor';
import CaseFileCheckmateSelectHelper from '../../../utilities/CaseFileCheckmateSelectHelper';
import CaseHelper from '../../../utilities/CaseHelper';
import { CaseNarrativeModal } from '../dialogs/CaseNarrativeModal';
import { CaseSearchControl } from './CaseSearchControl';
import { CheckmateDialog } from '../../shared/dialog';
import CheckmateSelect from '../../shared/CheckmateSelect';
import CheckmateSelectHelper from '../../../utilities/CheckmateSelectHelper';
import Common from '../../../stores/Common';
import { DisplayMessages } from '../../../utilities/DisplayMessages';
import { DocumentTitle } from '../../shared/DocumentTitle';
import { DocumentTypeEnum } from '../../../utilities/DbConstants';
import { IAssertionDescriptorModel } from '../../../interfaces/IAssertionDescriptor';
import { INoteModel } from '../../../interfaces/INote';
import { IValidation } from '../../../interfaces/IError';
import { Loader } from '../../shared/Loader';
import { LocalRoutes } from '../../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../shared/MainContentOverlay';
import { NoteEditor } from '../../notes/note-editor/NoteEditor';
import { PageWithAffixedHeader } from '../../../shared/PageWithAffixedHeader';
import { SameCaseStatusType } from '../../../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';
import Sort from '../../../stores/Sort';
import UIHelper from '../../../utilities/UIHelper';
import ValidateUtils from '../../../shared/validations';
import { lockableAssertionDescriptors } from '../../../pages/quad/SameCaseHelper';
import moment from 'moment';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useCaseManagersInZone } from '../../../shared/react-query-hooks/useCaseManagersInZone';
import { useCaseStatusTypesLookup } from '../../../shared/react-query-hooks/useCaseStatusTypesLookup';
import { useCheckCaseSameCase } from '../../../pages/quad/useCheckCaseSameCase';
import { useDefenseCounselsLookup } from '../../../shared/react-query-hooks/useDefenseCounselsLookup';
import { useGetCase } from '../useCaseFetch';
import { useGetDefaultUserOrg } from '../../../shared/react-query-hooks/useGetDefaultUserOrg';
import { useGetOrganizationSettings } from '../../../shared/react-query-hooks/useGetOrganizationSettings';
import { useNoteCategoryTypes } from '../../../shared/react-query-hooks/useNoteCategoryTypes';
import { useSaveCaseEasyUpdateMutation } from './useSaveCaseEasyUpdateMutation';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { useZoneGetAssertionDescriptors } from '../../../shared/react-query-hooks/useZoneGetAssertionDescriptors';
import { useZoneGetStaticItems } from '../../../shared/react-query-hooks/useZoneGetStaticItems';
import { useZoneUsers } from '../../../shared/react-query-hooks/useZoneUsers';
import { v4 as uuidv4 } from 'uuid';

export interface ICaseEasyUpdateProps {
    additionalHeaderText?: string;
    caseGuid?: string;
    defaultSelectedFields?: IMultiSelectOption[];
    documentTitle?: string;
    excludeNotes?: boolean;
    hidden?: boolean;
    onCancel?: (hadChanges: boolean) => void;
    onComplete?: (updatedCase: ICaseModel) => void;
    onSelectedFieldsChange?: (selectedFields: IMultiSelectOption[]) => void;
}

function CaseEasyUpdate(props: ICaseEasyUpdateProps) {
    const { user } = useAuthContext();
    const zoneContext = useZoneContext();
    
    const { key: refreshIdentifier } = useLocation();
    const navigate = useNavigate();

    const { zoneApiClient } = useZoneContext();
    const zoneUsersApi = useZoneUsers(zoneApiClient);
    const defenseCounselsApi = useDefenseCounselsLookup();
    const assertionDescriptorsApi = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Case,
        true
    );
    const staticItemsApi = useZoneGetStaticItems();
    const noteCategoryTypesApi = useNoteCategoryTypes();
    const organizationSettingsApi = useGetOrganizationSettings();
    const caseMangersApi = useCaseManagersInZone();
    const caseStatusTypesApi = useCaseStatusTypesLookup();
    const defaultOrgApi = useGetDefaultUserOrg();

    const [currentNarrativeCaseAssertion, setCurrentNarrativeCaseAssertion] = useState<
        ICaseAssertionModel | undefined
    >(undefined);

    const defenseCounsels: ILookupModel[] = React.useMemo(() => {
        return (
            defenseCounselsApi.data?.map((counsel) => ({
                displayName: counsel.name!,
                name: counsel.name!,
                id: 0,
                guid: counsel.guid!,
            })) || []
        );
    }, [defenseCounselsApi.data]);

    const [addNewCase, setAddNewCase] = useState<boolean>(false);
    const [adGroupFieldToDelete, setAdGroupFieldToDelete] = useState<
        IMultiSelectOption | undefined
    >(undefined);
    const [caseFieldsAll, setCaseFieldsAll] = useState<IMultiSelectOption[]>([]);
    const [selectedCaseGuid, setSelectedCaseGuid] = useState<string | undefined>(props.caseGuid);
    const [currentCase, setCurrentCase] = useState<ICaseModel>({} as ICaseModel);
    const [currentCaseExpert, setCurrentCaseExpert] = useState<
        IViewCaseExpertModelWithDefaultEdit | null | undefined
    >(undefined);
    const [currentFavoriteDetailsString, setCurrentFavoriteDetailsString] = useState<string>('');

    const [currentNote, setCurrentNote] = useState<INoteModel | undefined>(undefined);
    const [easyUpdateFieldsSelected, setEasyUpdateFieldsSelected] = useState<IMultiSelectOption[]>(
        props.defaultSelectedFields || []
    );
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [noteAdded, setNoteAdded] = useState<boolean>(false);
    const [openNoteEditor, setOpenNoteEditor] = useState<boolean>(false);
    const [originalCase, setOriginalCase] = useState<ICaseModel>({});
    const [pendingResponse, setPendingResponse] = useState<boolean>(false);

    const [systemGeneratedIds, setSystemGeneratedIds] = useState<string[]>([]);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [userDefaultSettingsJson, setUserDefaultSettingsJson] = useState<string | undefined>(
        undefined
    );
    const [validation, setValidation] = useState<IValidation>({} as IValidation);

    const caseDetailsApi = useGetCase(selectedCaseGuid);
    const saveEasyUpdateMutation = useSaveCaseEasyUpdateMutation();
    const checkSameCaseQuery = useCheckCaseSameCase(selectedCaseGuid);
    const isPendingCaseInSameCaseGroup =
        checkSameCaseQuery?.data?.sameCaseStatusTypeId === SameCaseStatusType.Pending;

    const isApprovedCaseInSameCaseGroup =
        checkSameCaseQuery?.data?.sameCaseStatusTypeId === SameCaseStatusType.Approved;

    const staticItems = staticItemsApi.data || [];
    const assertionDescriptors = assertionDescriptorsApi.data || [];
    const caseStatuses = caseStatusTypesApi.data || [];
    const zoneUsers = zoneUsersApi.data || [];
    const noteCategoryTypes = noteCategoryTypesApi.data || [];

    const addStaticItemsToQueryCriteriaList = (queryCriteriaList: IMultiSelectOption[]) => {
        let criteriaListCopy = cloneDeep(queryCriteriaList);

        const isAuthorizedToEditCaseFile = Authorization.userHasRight(
            UserRightsEnum.EditCaseFile,
            user,
            zoneContext.zoneGuid
        );

        if (isAuthorizedToEditCaseFile && staticItems.length > 0) {
            const staticItemFilter = (x: IStaticItemLookupModel) =>
                x.id !== CaseFileStaticItems.NotesSection.Id &&
                x.id !== CaseFileStaticItems.DepositionSection.Id &&
                x.id !== CaseFileStaticItems.ExpertsSection.Id &&
                x.id !== CaseFileStaticItems.DefenseCounselSection.Id &&
                x.displayItemTypeId != DisplayItemTypeEnum.ReadOnlySingleField;

            criteriaListCopy = criteriaListCopy.concat(
                staticItems.filter(staticItemFilter).map((item: ILookupModel) => ({
                    id: item.id,
                    label: item.alternateDisplayName || item.displayName || item.name || '',
                    value: item.name || '',
                }))
            );
        }

        if (!props.excludeNotes) {
            criteriaListCopy.push({
                id: CaseFileStaticItems.NotesSection.Id,
                label: CaseHelper.getCaseFileStaticItemDisplayName(
                    staticItems,
                    CaseFileStaticItems.NotesSection.Id
                ),
                value: UIHelper.getName(staticItems, CaseFileStaticItems.NotesSection.Id),
            });
        }

        const isAuthorizedToDepoCRUD = Authorization.userHasAtLeastOneRight(
            user,
            [
                UserRightsEnum.AddDeposition,
                UserRightsEnum.EditDeposition,
                UserRightsEnum.DeleteDeposition,
            ],
            zoneContext.zoneGuid
        );

        if (isAuthorizedToDepoCRUD) {
            criteriaListCopy.push({
                id: CaseFileStaticItems.DepositionSection.Id,
                label: CaseHelper.getCaseFileStaticItemDisplayName(
                    staticItems,
                    CaseFileStaticItems.DepositionSection.Id
                ),
                value: UIHelper.getName(staticItems, CaseFileStaticItems.DepositionSection.Id),
            });
        }

        const isAuthorizedToViewExperts = Authorization.userHasRight(
            UserRightsEnum.ViewExpert,
            user,
            zoneContext.zoneGuid
        );

        if (isAuthorizedToViewExperts) {
            criteriaListCopy.push({
                id: CaseFileStaticItems.ExpertsSection.Id,
                label: CaseHelper.getCaseFileStaticItemDisplayName(
                    staticItems,
                    CaseFileStaticItems.ExpertsSection.Id
                ),
                value: UIHelper.getName(staticItems, CaseFileStaticItems.ExpertsSection.Id),
            });
        }

        return criteriaListCopy;
    };

    const addADsToQueryCriteriaList = (queryCriteriaList: IMultiSelectOption[]) => {
        const queryCriteriaListCopy = cloneDeep(queryCriteriaList);

        assertionDescriptors?.forEach((item: IAssertionDescriptorModel) => {
            let exclude = false;
            const match = CustomLogicAssertionDescriptors.find(
                (x) => x.Name.toUpperCase() == item.name!.toUpperCase()
            );

            if (match && match.ReadOnly) {
                exclude = true;
            }

            if (!exclude) {
                queryCriteriaListCopy.push({
                    id: 0,
                    label: item.alternateDisplayName || item.displayName!,
                    value: item.name!,
                    guid: item.guid,
                });
            }
        });

        return queryCriteriaListCopy;
    };

    const getCriteriaList = () => {
        let queryCriteriaList: IMultiSelectOption[] = [];
        queryCriteriaList = addStaticItemsToQueryCriteriaList(queryCriteriaList);
        queryCriteriaList = addADsToQueryCriteriaList(queryCriteriaList);
        queryCriteriaList = queryCriteriaList.sort(Sort.compareValues('label'));
        return queryCriteriaList;
    };

    const getUpdatedEasyUpdateFields = (
        currentCase: ICaseModel,
        easyUpdateFieldsSelected: IMultiSelectOption[]
    ): IMultiSelectOption[] => {
        const easyUpdateFieldsSelectedCopy = cloneDeep(easyUpdateFieldsSelected);
        const additionalCaseAssertions = CaseHelper.getAdditionalCaseAssertionFields(
            currentCase,
            assertionDescriptors
        );

        const autoPopulatedGuids = additionalCaseAssertions.map(
            (field) => field.assertionDescriptorGuid ?? ''
        );

        if (additionalCaseAssertions.length > 0) {
            // Since Case Status is NOT an Assertion Descriptor, this is a hack that's carried over from before to ensure that Case Status
            // is always added to the selected fields if the Resolution Agreement Date is present.
            //
            // The downside is, it is always added if at least one addtional field is needed. So if in future we have other
            // fields that do not need Case Status to be present, we would need to rethink how addtional fields are handled.
            const isCaseStatusStaticItemAlreadySelected = easyUpdateFieldsSelectedCopy.some(
                (x) => x.id === CaseFileStaticItems.CaseStatus.Id
            );

            if (!isCaseStatusStaticItemAlreadySelected) {
                const caseStatusCaseAssertion = {
                    id: CaseFileStaticItems.CaseStatus.Id,
                    label: CaseHelper.getCaseFileStaticItemDisplayName(
                        staticItems,
                        CaseFileStaticItems.CaseStatus.Id
                    ),
                    value: UIHelper.getName(staticItems, CaseFileStaticItems.CaseStatus.Id),
                    guid: uuidv4(),
                };

                easyUpdateFieldsSelectedCopy.push(caseStatusCaseAssertion);
                autoPopulatedGuids.push(caseStatusCaseAssertion.guid);
            }

            additionalCaseAssertions.map((ca) => {
                const foundAD = assertionDescriptors.find(
                    (ad) => ad.guid === ca.assertionDescriptorGuid
                );

                const adAlreadySelected = easyUpdateFieldsSelectedCopy.some(
                    (x) => x.guid?.toUpperCase() === ca.assertionDescriptorGuid!.toUpperCase()
                );

                if (foundAD && !adAlreadySelected) {
                    easyUpdateFieldsSelectedCopy.push({
                        id: 0,
                        label:
                            foundAD?.alternateDisplayName ??
                            foundAD?.displayName ??
                            foundAD?.name ??
                            '',
                        value: foundAD?.name ?? '',
                        guid: ca.assertionDescriptorGuid,
                    });
                }
            });
        }
        setSystemGeneratedIds(autoPopulatedGuids);

        ADGroups.forEach((adGroup) => {
            CaseHelper.handleGroupFieldsForEasyUpdate(
                currentCase,
                easyUpdateFieldsSelectedCopy,
                adGroup,
                assertionDescriptors
            );
        });

        return easyUpdateFieldsSelectedCopy;
    };

    const resetAllState = () => {
        setPendingResponse(false);
        setCaseFieldsAll([]);
        setEasyUpdateFieldsSelected([]);
        setValidation({});
        setAddNewCase(false);
        setOriginalCase({});
        setCurrentCase({ caseAssertions: [] } as ICaseModel);
        setOpenNoteEditor(false);
        setNoteAdded(false);
        setCurrentFavoriteDetailsString('');
    };

    const refreshCaseFieldValue = (currentCase: ICaseModel, item: IMultiSelectOption) => {
        const assertionDescriptorMatch = assertionDescriptors.find((x) => x.guid === item.guid);

        if (item.guid && assertionDescriptorMatch) {
            if (
                currentCase.caseAssertions &&
                originalCase.caseAssertions &&
                assertionDescriptorMatch
            ) {
                const currentCaseCaseAssertionMatch = currentCase.caseAssertions.find(
                    (x) => x.assertionDescriptorGuid === assertionDescriptorMatch.guid
                );

                const originalCaseCaseAssertionMatch = originalCase.caseAssertions.find(
                    (x) => x.assertionDescriptorGuid === assertionDescriptorMatch.guid
                );

                if (currentCaseCaseAssertionMatch && originalCaseCaseAssertionMatch) {
                    Object.assign(currentCaseCaseAssertionMatch, originalCaseCaseAssertionMatch);
                } else if (currentCaseCaseAssertionMatch) {
                    Object.assign(currentCaseCaseAssertionMatch, {
                        assertionDescriptorValueGuid: undefined,
                        booleanValue: undefined,
                        dateTimeValue: undefined,
                        numericValue: undefined,
                        textValue: undefined,
                        userOverride: undefined,
                    } as ICaseAssertionModel);
                }
            }
        } else {
            switch (item.id) {
                case CaseFileStaticItems.CaseNumber.Id:
                    currentCase.caseNumber = originalCase.caseNumber;
                    break;
                case CaseFileStaticItems.UniqueCaseID.Id:
                    currentCase.uniqueCaseId = originalCase.uniqueCaseId;
                    break;
                case CaseFileStaticItems.CaseStatus.Id:
                    currentCase.caseStatus = cloneDeep(originalCase.caseStatus);
                    break;
                case CaseFileStaticItems.CaseManager.Id:
                    currentCase.caseManagerUser = cloneDeep(originalCase.caseManagerUser);
                    break;
                case CaseFileStaticItems.AlternateCaseManager.Id:
                    currentCase.alternateCaseManagerUser = cloneDeep(
                        originalCase.alternateCaseManagerUser
                    );
                    break;
                case CaseFileStaticItems.LocalCounsel.Id:
                    currentCase.defenseCounsels = cloneDeep(originalCase.defenseCounsels);
                    break;
                case CaseFileStaticItems.CasePriority.Id:
                    currentCase.priority = originalCase.priority;
                    break;
                case CaseFileStaticItems.NotesSection.Id:
                    setNoteAdded(false);
                    setCurrentNote({
                        status: { id: NoteStatusTypes.Open },
                        purpose: { id: NotePurposeTypesEnum.Info },
                    } as INoteModel);
                    break;
            }
        }
    };

    const deleteFields = (itemsToRemove: IMultiSelectOption[]) => {
        // While deleting the case Field from the grid, remove that value from the list of selected fields.
        // Also, set its value in the "currentCase" variable to the original value from the "originalCase" variable so that next time they select the same field, the UI displays the correct value

        const caseCopy = cloneDeep(currentCase);
        let selectedFields: IMultiSelectOption[] = cloneDeep(easyUpdateFieldsSelected);

        itemsToRemove.forEach((item) => {
            const adGroupForItem =
                ADGroups.find((group) => group.includes(item.guid?.toUpperCase() ?? '')) ?? [];

            // If an item is part of an ADGroup, we need to remove all group members as well
            if (adGroupForItem) {
                const adGroupItems = selectedFields.filter((field) =>
                    adGroupForItem
                        .map((guid) => guid.toUpperCase())
                        .includes(field.guid?.toUpperCase() ?? '')
                );
                itemsToRemove = itemsToRemove.concat([...adGroupItems]);
            }

            selectedFields = selectedFields.filter((field) => {
                if (field.id > 0) {
                    return !itemsToRemove.map((item) => item.id).includes(field.id);
                } else {
                    return !itemsToRemove.map((item) => item.guid).includes(field.guid);
                }
            });

            if (caseCopy) {
                itemsToRemove.forEach((itemToRemove) => {
                    refreshCaseFieldValue(caseCopy, itemToRemove);
                });
            }
        });

        setEasyUpdateFieldsSelected(selectedFields);
        setCurrentCase(caseCopy);
    };

    const getValue = (item: IMultiSelectOption): any => {
        if (item.guid && assertionDescriptors.filter((x) => x.guid == item.guid).length > 0) {
            const assertionDescriptorMatch = assertionDescriptors.filter(
                (x) => x.guid == item.guid
            )[0];
            if (
                assertionDescriptorMatch.valueDisplayType?.id ===
                AssertionDescriptorValueDisplayTypes.MultiSelect.Value
            ) {
                return CaseHelper.getMultiSelectSelectedValues(
                    assertionDescriptorMatch,
                    currentCase
                );
            } else {
                if (currentCase.caseAssertions) {
                    const caseAssertionMatch = currentCase.caseAssertions.find(
                        (x) => x.assertionDescriptorGuid == assertionDescriptorMatch.guid
                    );
                    if (caseAssertionMatch) {
                        switch (assertionDescriptorMatch?.valueSourceType!.id) {
                            case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                            case AssertionDescriptorValueSourceTypeEnum.Implied.Value:
                                return CaseFileCheckmateSelectHelper.getCheckmateSelectValue(
                                    caseAssertionMatch,
                                    assertionDescriptors
                                );
                            case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                                return CheckmateSelectHelper.getBooleanOptionValue(
                                    caseAssertionMatch.booleanValue
                                );
                            default: {
                                const isDateTimeField =
                                    assertionDescriptorMatch?.valueDisplayType?.id ===
                                    AssertionDescriptorDisplayValueTypeEnum.DateTime.Value;

                                if (isDateTimeField) {
                                    return Common.dateFormat(caseAssertionMatch.dateTimeValue);
                                }

                                const numericValueDisplayTypes = [
                                    AssertionDescriptorDisplayValueTypeEnum.Currency.Value,
                                    AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value,
                                    AssertionDescriptorDisplayValueTypeEnum.Number.Value,
                                    AssertionDescriptorDisplayValueTypeEnum.Decimal.Value,
                                ];

                                const isNumericDisplayType = numericValueDisplayTypes.includes(
                                    assertionDescriptorMatch?.valueDisplayType?.id ?? -1
                                );

                                if (isNumericDisplayType) {
                                    return caseAssertionMatch.numericValue;
                                }

                                return caseAssertionMatch.textValue ?? '';
                            }
                        }
                    }
                }
            }
        } else {
            switch (item.id) {
                case CaseFileStaticItems.CaseNumber.Id:
                    return currentCase.caseNumber ? currentCase.caseNumber : '';
                case CaseFileStaticItems.UniqueCaseID.Id:
                    return currentCase.uniqueCaseId ? currentCase.uniqueCaseId : '';
                case CaseFileStaticItems.CaseStatus.Id:
                    if (currentCase.caseStatus) {
                        if (currentCase.caseStatus.status) {
                            let filter = caseStatuses.filter(
                                (x) => x.status && x.status.id == currentCase.caseStatus!.status.id
                            );
                            if (currentCase.caseStatus.subStatus)
                                filter = filter.filter(
                                    (x) =>
                                        x.subStatus &&
                                        x.subStatus.id == currentCase.caseStatus!.subStatus.id
                                );
                            if (filter.length > 0) {
                                return CaseFileCheckmateSelectHelper.getCaseStatusValue(
                                    caseStatuses,
                                    filter[0].id!
                                );
                            }
                        }
                    }
                    return undefined;
                case CaseFileStaticItems.CaseManager.Id: {
                    const a1Guid =
                        currentCase.caseManagerUser && currentCase.caseManagerUser.guid
                            ? currentCase.caseManagerUser.guid
                            : '';
                    return CheckmateSelectHelper.getSelectedUserValue(zoneUsers, a1Guid);
                }
                case CaseFileStaticItems.AlternateCaseManager.Id: {
                    const a2Guid =
                        currentCase.alternateCaseManagerUser &&
                        currentCase.alternateCaseManagerUser.guid
                            ? currentCase.alternateCaseManagerUser.guid
                            : '';
                    return CheckmateSelectHelper.getSelectedUserValue(zoneUsers, a2Guid);
                }
                case CaseFileStaticItems.LocalCounsel.Id:
                    if (currentCase.defenseCounsels) {
                        const localCounsel = currentCase.defenseCounsels.filter(
                            (x) => x.priority == 1
                        );
                        if (localCounsel.length > 0)
                            return CheckmateSelectHelper.getSelectedOrgValue(
                                defenseCounsels,
                                localCounsel[0].guid!
                            );
                    }
                    break;
                case CaseFileStaticItems.CasePriority.Id:
                    return CheckmateSelectHelper.getBooleanOptionValue(currentCase.priority);
            }
        }

        return '';
    };

    const getAllEasyUpdateFieldsForMultiSelect = () => {
        // Do not allow case Notes for a new case.
        let fieldsList: IMultiSelectOption[] = cloneDeep(caseFieldsAll);

        if (addNewCase) {
            const fieldsToRemove = [
                CaseFileStaticItems.NotesSection.Id,
                CaseFileStaticItems.DepositionSection.Id,
                CaseFileStaticItems.ExpertsSection.Id,
            ];

            fieldsList = fieldsList.filter((field) => !fieldsToRemove.includes(field.id));
        }

        // need to mark fields that are part of an AD Group as read only
        const flatAdGroups = ADGroups.flatMap((group) => group);
        fieldsList.forEach((field) => {
            field.preventRemoval = flatAdGroups.includes(field.guid?.toUpperCase() ?? '');
        });

        return fieldsList;
    };

    // handlers
    const handleCaseExpertEditorSave = (updatedCaseExpert: ICaseExpertViewDetailModel) => {
        const caseExpertsResultsCopy = cloneDeep(currentCase.caseExperts);
        const found = caseExpertsResultsCopy?.find((ce) => ce.guid === updatedCaseExpert.guid);

        if (found) {
            found.disciplines = updatedCaseExpert.expert.disciplines ?? [];
            found.leadDefendant = updatedCaseExpert.leadDefendant ?? '';
            found.coDefendants = updatedCaseExpert.coDefendants ?? '';
            found.expertReportStatusTypeId = updatedCaseExpert.expertReportStatusTypeId ?? 0;
            found.expertRetainedByTypeId = updatedCaseExpert.expertRetainedByTypeId ?? 0;
            found.purposeOfRetention = updatedCaseExpert.purposeOfRetention;
        }

        setCurrentCase((prev) => ({ ...prev, caseExperts: caseExpertsResultsCopy }));
    };

    const handleAddNewCase = async () => {
        const currentCase: ICaseModel = {
            organization: defaultOrgApi.data,
            caseAssertions: [],
        };

        let easyUpdateFieldsSelectedCopy: IMultiSelectOption[] =
            cloneDeep(easyUpdateFieldsSelected);

        const idsToRemove = [
            CaseFileStaticItems.NotesSection.Id,
            CaseFileStaticItems.DepositionSection.Id,
        ];

        easyUpdateFieldsSelectedCopy = easyUpdateFieldsSelectedCopy.filter(
            (x) => !idsToRemove.includes(x.id)
        );

        // Automatically add required fields to the easyUpdateFieldsSelected List
        for (const item of caseFieldsAll) {
            const itemGuid = (item.guid || '').toUpperCase();
            const firstNameGuid = AssertionDescriptors.InjuredPartyFirstName.Guid.toUpperCase();
            const lastNameGuid = AssertionDescriptors.InjuredPartyLastName.Guid.toUpperCase();

            const isRequiredField =
                item.id === CaseFileStaticItems.CaseStatus.Id ||
                item.id === CaseFileStaticItems.CaseNumber.Id ||
                itemGuid === firstNameGuid ||
                itemGuid === lastNameGuid;

            if (isRequiredField) {
                const alreadyExists = easyUpdateFieldsSelectedCopy.some(
                    (x) => x.value.toUpperCase() === item.value.toUpperCase()
                );

                if (!alreadyExists) {
                    easyUpdateFieldsSelectedCopy.push(item);
                }
            }

            const isInjuredPartyName = itemGuid === firstNameGuid || itemGuid === lastNameGuid;

            if (isInjuredPartyName) {
                const assertionExists = currentCase.caseAssertions?.some(
                    (a) => a.assertionDescriptorGuid?.toUpperCase() === itemGuid
                );

                if (!assertionExists) {
                    currentCase.caseAssertions!.push({
                        assertionDescriptorGuid: item.guid,
                    });
                }
            }
        }

        setAddNewCase(true);
        setCurrentCase(currentCase);
        setOriginalCase(currentCase);
        setEasyUpdateFieldsSelected(easyUpdateFieldsSelectedCopy);
        setValidation({});
    };

    const handleApplyFavoriteSelected = (settingJson?: string, isDefault?: boolean) => {
        if (settingJson) {
            let easyUpdateFieldsSelected: IMultiSelectOption[] = JSON.parse(settingJson);

            const adsToRemove: string[] = [];
            for (let i = 0; i < easyUpdateFieldsSelected.length; i++) {
                const item = easyUpdateFieldsSelected[i];
                // Get the Latest Display Name since the Favorite might have been added with an old value
                if (item.guid) {
                    const match = assertionDescriptors.find(
                        (x) => x.guid!.toUpperCase() === item.guid!.toUpperCase()
                    );
                    if (match) {
                        item.label = match.alternateDisplayName || match.displayName || match.name!;
                    } else adsToRemove.push(item.guid);
                }
            }

            for (let index = 0; index < adsToRemove.length; index++) {
                const element = adsToRemove[index];
                easyUpdateFieldsSelected = easyUpdateFieldsSelected.filter(
                    (x) => x.guid != element
                );
            }

            if (addNewCase || props.excludeNotes)
                easyUpdateFieldsSelected = easyUpdateFieldsSelected.filter(
                    (x) => x.id != CaseFileStaticItems.NotesSection.Id
                );

            setEasyUpdateFieldsSelected(easyUpdateFieldsSelected);
            setUserDefaultSettingsJson((prev) => (isDefault ? settingJson : prev));
        }
    };

    const handleBackClick = () => {
        if (typeof props.onCancel === 'function') {
            props.onCancel(hasChanges);
        }
    };

    const handleFavoriteSelected = (settingJson?: string) => {
        let selectedFilters = '';
        if (settingJson) {
            const easyUpdateFieldsSelected: IMultiSelectOption[] = JSON.parse(settingJson);
            if (easyUpdateFieldsSelected.length > 0)
                easyUpdateFieldsSelected.map((item: IMultiSelectOption) => {
                    const match = caseFieldsAll.find((x) => x.value === item.value);
                    if (match) selectedFilters += match.label + '\n';
                });
        }

        setCurrentFavoriteDetailsString(selectedFilters);
    };

    const handleClearAll = () => {
        deleteFields(easyUpdateFieldsSelected);

        setUserDefaultSettingsJson(undefined);
        setEasyUpdateFieldsSelected([]);
        setAddNewCase(false);
        setValidation({});
        setCurrentCase(originalCase);
    };

    const handleSaveCase = async () => {
        const currentCaseCopy = cloneDeep(currentCase);
        const validationCopy = cloneDeep(validation);

        if (!addNewCase && !currentCaseCopy.guid) {
            validationCopy.model = ['Please select an existing case to update (or) Add a new case'];
            setValidation(validationCopy);
            return;
        }

        if (easyUpdateFieldsSelected.length === 0) {
            validationCopy.model = ['Please select at least one field to update'];
            setValidation(validationCopy);
            return;
        }

        let easyUpdateFieldsSelectedCopy: IMultiSelectOption[] =
            cloneDeep(easyUpdateFieldsSelected);

        // If user somehow deleted/removed the required date field for Resolved Cases, add it back.
        easyUpdateFieldsSelectedCopy = getUpdatedEasyUpdateFields(
            currentCaseCopy,
            easyUpdateFieldsSelectedCopy
        );

        // Validations
        if (addNewCase) {
            const validationErrors: string[] = [];

            if (
                !easyUpdateFieldsSelectedCopy.some(
                    (x) => x.id === CaseFileStaticItems.CaseNumber.Id
                )
            ) {
                validationErrors.push('Case Number is required for a new case');
            }

            if (
                !easyUpdateFieldsSelectedCopy.some(
                    (x) => x.id === CaseFileStaticItems.CaseStatus.Id
                )
            ) {
                validationErrors.push('Case Status is required for a new case');
            }

            if (!currentCaseCopy.caseAssertions || currentCaseCopy.caseAssertions.length === 0) {
                validationErrors.push(
                    'Injured Party First & Last Name are required for a new case'
                );
            } else if (
                !currentCaseCopy.caseAssertions.some(
                    (x) =>
                        x.assertionDescriptorGuid?.toUpperCase() ===
                        AssertionDescriptors.InjuredPartyFirstName.Guid
                )
            ) {
                validationErrors.push('Injured Party First Name is required for a new case');
            } else if (
                !currentCaseCopy.caseAssertions.some(
                    (x) =>
                        x.assertionDescriptorGuid?.toUpperCase() ===
                        AssertionDescriptors.InjuredPartyLastName.Guid
                )
            ) {
                validationErrors.push('Injured Party Last Name is required for a new case');
            }

            if (validationErrors.length > 0) {
                validationCopy.model = validationErrors;
            }
        }

        for (let i = 0; i < easyUpdateFieldsSelectedCopy.length; i++) {
            const item = easyUpdateFieldsSelectedCopy[i];
            item.validationError = undefined;

            if (item.guid && assertionDescriptors.filter((x) => x.guid == item.guid).length > 0) {
                const assertionDescriptorMatch = assertionDescriptors.find(
                    (x) => x.guid == item.guid
                );
                if (currentCaseCopy.caseAssertions) {
                    const caseAssertionMatch = currentCaseCopy.caseAssertions.find(
                        (x) => x.assertionDescriptorGuid === assertionDescriptorMatch?.guid
                    );
                    if (caseAssertionMatch) {
                        const isLastNameAD =
                            assertionDescriptorMatch?.guid!.toUpperCase() ===
                            AssertionDescriptors.InjuredPartyLastName.Guid;

                        const isFirstNameAD =
                            assertionDescriptorMatch?.guid!.toUpperCase() ===
                            AssertionDescriptors.InjuredPartyFirstName.Guid;

                        const caseAssertionTextValueIsEmpty = !caseAssertionMatch.textValue?.trim();

                        if (isLastNameAD && caseAssertionTextValueIsEmpty) {
                            item.validationError = 'Injured Party Last Name is required';
                        } else if (isFirstNameAD && caseAssertionTextValueIsEmpty) {
                            item.validationError = 'Injured Party First Name is required';
                        } else if (
                            assertionDescriptorMatch?.name!.includes('SSN') &&
                            caseAssertionMatch.textValue &&
                            !ValidateUtils.isValidSSN(caseAssertionMatch.textValue!)
                        ) {
                            item.validationError =
                                'Invalid ' +
                                assertionDescriptorMatch.displayName +
                                ".  Enter a valid SSN in the format XXX-XX-XXXX (Use 0's if you only have last 4 of the SSN i.e. 000 - 000 - 1234)";
                        } else if (assertionDescriptorMatch?.valueDisplayType) {
                            switch (assertionDescriptorMatch.valueDisplayType.id) {
                                case AssertionDescriptorValueDisplayTypes.DateTime.Value:
                                    const value = caseAssertionMatch.dateTimeValue;

                                    if (value) {
                                        const enteredDate = moment(value);
                                        const minDate = moment('1753-01-01');
                                        const maxDate = moment('9999-12-31');

                                        if (!Common.isValidDate(enteredDate.format('YYYY-MM-DD'))) {
                                            item.validationError = 'Enter a valid date';
                                        } else if (enteredDate.isBefore(minDate)) {
                                            item.validationError = `Date must be after ${minDate.format('MMMM D, YYYY')}`;
                                        } else if (enteredDate.isAfter(maxDate)) {
                                            item.validationError = `Date must be before ${maxDate.format('MMMM D, YYYY')}`;
                                        }
                                    } else if (item.externalValidationError) {
                                        item.validationError = item.externalValidationError;
                                    }
                                    break;
                                case AssertionDescriptorValueDisplayTypes.Currency.Value:
                                    if (
                                        caseAssertionMatch.numericValue &&
                                        isNaN(Number(caseAssertionMatch.numericValue))
                                    ) {
                                        item.validationError = 'Enter a valid number';
                                    }
                                    break;
                                default:
                            }
                        }
                    }
                }
            } else {
                switch (item.id) {
                    case CaseFileStaticItems.CaseNumber.Id:
                        if (
                            !currentCaseCopy.caseNumber ||
                            currentCaseCopy.caseNumber.trim() == ''
                        ) {
                            item.validationError = 'Case Number is required';
                        }
                        break;
                    case CaseFileStaticItems.CaseStatus.Id:
                        if (
                            !currentCaseCopy.caseStatus ||
                            !currentCaseCopy.caseStatus.status ||
                            currentCaseCopy.caseStatus.status.id == 0
                        ) {
                            // AR - TO DO - Status Cleanup - Also add Mapping validation
                            item.validationError = 'Status is required';
                        }
                        break;
                }
            }
        }

        if (easyUpdateFieldsSelectedCopy.filter((x) => x.validationError).length > 0) {
            setValidation(validationCopy);
            setEasyUpdateFieldsSelected(easyUpdateFieldsSelectedCopy);
            return;
        }

        if (noteAdded) {
            currentCaseCopy.notes = [currentNote!];
        }

        setValidation({});

        /*
        ASSUMPTIONS FOR THE EASY UPDATE
       - STATUS -  Required field in the Model. Hence latest value from the model will be updated in the DB
       - CASE NUMBER -  Required field in the Model. Hence latest value from the model will be updated in the DB
       - CASE MANAGER - For an existing case, if CaseManager object is NULL, its ignored. If CaseManagerUser object is NOT NULL but Guid = NULL, then its considered that the user is deleting the CaseManager Value
       - LOCAL COUNSEL - For an existing case, if DefenseCounsels list is empty or item with Priority 1 doesnt exist, then ignored. If item with Priority 1 exists but Guid = Guid.Empty, then its considered that the user is deleting the Local Counsel
       - NOTE: Only new notes can be added
        */

        // remove case experts and notes from patch
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { caseExperts, notes, ...restOfCase } = currentCaseCopy;

        CaseHelper.updateUserOverrideFieldForCaseAssertions(restOfCase);

        setPendingResponse(true);

        saveEasyUpdateMutation
            .mutateAsync(restOfCase)
            .then((savedCase) => {
                setPendingResponse(false);

                // We only need the updated case Details in case of Easy Update from case List. So that the case details can be sent back to the parent control to update the case List
                // No need to preserve the Fields in case of creating a new case.

                const updatedCase: ICaseModel = savedCase ?? { caseAssertions: [] };

                setCurrentCase(updatedCase);
                setOriginalCase(cloneDeep(updatedCase));
                setNoteAdded(false);
                setCurrentNote(undefined);

                if (typeof props.onComplete === 'function') {
                    props.onComplete(updatedCase);
                }

                setPendingResponse(false);
                showSuccessToast(<span>The case was successfully saved!</span>);
                if (addNewCase && typeof navigate === 'function') {
                    zoneContext.zoneNavigate(LocalRoutes.CaseEasyUpdate);
                }
                setHasChanges(true);
            })
            .catch((error: Error) => {
                setPendingResponse(false);
                showErrorToast(
                    `An error occurred while saving the case for - ${currentCase.caseName}`
                );

                setValidation(ValidateUtils.parseErrors([], error.message));
            });
    };

    const handleCancelNoteEditor = () => {
        // Since we update the CurrentCase.Notes directly on change of any field in the NoteEditor, on Cancel, reset all changes
        const currentCaseCopy = currentCase;
        if (currentCase && originalCase && originalCase.notes) {
            currentCaseCopy.notes = JSON.parse(JSON.stringify(originalCase.notes));
        }

        setCurrentNote(undefined);
        setOpenNoteEditor(false);
        setCurrentCase(currentCaseCopy);
    };

    const handleSaveNoteComplete = (noteItem: INoteModel) => {
        const currentCaseCopy = cloneDeep(currentCase);

        let matchFound = false;
        let caseNotes = currentCaseCopy.notes || [];
        for (let i = 0; i < caseNotes.length; i++) {
            if (caseNotes[i].guid == noteItem.guid) {
                matchFound = true;
                caseNotes[i] = cloneDeep(noteItem);
                break;
            }
        }

        if (!matchFound) caseNotes.push(cloneDeep(noteItem));

        caseNotes = caseNotes.sort(Sort.compareDate('modifiedDate', undefined, 'desc'));
        currentCaseCopy.notes = caseNotes;

        setPendingResponse(false);
        setOpenNoteEditor(false);
        setCurrentNote({
            status: { id: NoteStatusTypes.Open },
            purpose: { id: NotePurposeTypesEnum.Info },
        } as INoteModel);
        setCurrentCase(currentCaseCopy);
        setOriginalCase(cloneDeep(currentCaseCopy));
    };

    const handleClearSearchControl = () => {
        setCurrentCase({ caseAssertions: [] } as ICaseModel);
        setSelectedCaseGuid(undefined);
        setValidation({});
    };

    const handleChange = (
        event:
            | React.ChangeEvent<HTMLInputElement>
            | React.ChangeEvent<HTMLSelectElement>
            | React.ChangeEvent<HTMLTextAreaElement>,
        item?: IMultiSelectOption
    ) => {
        const { name, value } = event.target;

        if (item) item.validationError = '';

        if (name === 'noteContent') {
            if (currentNote) {
                const currentNoteCopy = currentNote;
                currentNoteCopy.content = value;
                setCurrentNote(currentNoteCopy);
                setValidation({});
            }
            return;
        }

        if (item) {
            const currentCaseCopy = cloneDeep(currentCase);
            const assertionDescriptorMatch = assertionDescriptors.find((x) => x.guid == item.guid);

            if (assertionDescriptorMatch) {
                currentCaseCopy.caseAssertions = currentCaseCopy.caseAssertions ?? [];
                const caseAssertionMatch = currentCaseCopy.caseAssertions.find(
                    (x) => x.assertionDescriptorGuid == assertionDescriptorMatch.guid
                );

                if (caseAssertionMatch) {
                    caseAssertionMatch.userOverride = true;
                    CaseHelper.setCaseAssertionValue(
                        caseAssertionMatch,
                        assertionDescriptorMatch,
                        value
                    );
                } else if (value) {
                    const newCaseAssertion: ICaseAssertionModel = {
                        assertionDescriptorGuid: assertionDescriptorMatch.guid,
                        userOverride: true,
                    };
                    CaseHelper.setCaseAssertionValue(
                        newCaseAssertion,
                        assertionDescriptorMatch,
                        value
                    );
                    currentCaseCopy.caseAssertions.push(newCaseAssertion);
                }
            } else {
                switch (item.id) {
                    case CaseFileStaticItems.CaseNumber.Id:
                        currentCaseCopy.caseNumber = value;
                        break;
                    case CaseFileStaticItems.UniqueCaseID.Id:
                        currentCaseCopy.uniqueCaseId = value;
                        break;
                    case CaseFileStaticItems.LocalCounsel.Id:
                        // If first ever time adding any Defense Counsel; just add
                        if (!currentCaseCopy.defenseCounsels) {
                            if (value)
                                currentCaseCopy.defenseCounsels = [{ guid: value, priority: 1 }];
                            break;
                        }
                        // If no Local Counsel, Add
                        if (
                            currentCaseCopy.defenseCounsels.filter((x) => x.priority == 1)
                                .length === 0
                        ) {
                            if (value)
                                currentCaseCopy.defenseCounsels.push({
                                    guid: value,
                                    priority: 1,
                                });
                            break;
                        }
                        // Update previous value of Local Counsel either to a different value or remove all together
                        currentCaseCopy.defenseCounsels.filter((x) => x.priority == 1)[0].guid =
                            value ? value : undefined; // If Value = "", that means the Local Counsel is being removed. Hence set guid = undefined
                        break;
                    case CaseFileStaticItems.CasePriority.Id:
                        currentCaseCopy.priority = value == 'true' ? true : undefined;
                        break;
                }
            }

            const updatedEasyUpdateFieldsSelected = getUpdatedEasyUpdateFields(
                currentCaseCopy,
                easyUpdateFieldsSelected
            );

            CaseDataCustomLogicHelper.Run(currentCaseCopy, assertionDescriptors);
            setCurrentCase(currentCaseCopy);
            setEasyUpdateFieldsSelected(updatedEasyUpdateFieldsSelected);
            setValidation({});
        }
    };

    const handleEasyUpdateFieldsSelected = (optionsList: any[]) => {
        const previousFieldsSelected: IMultiSelectOption[] = cloneDeep(easyUpdateFieldsSelected);

        const currentFieldsSelected: IMultiSelectOption[] = optionsList;
        const itemDeleted = previousFieldsSelected.length > currentFieldsSelected.length;

        const currentCaseCopy = cloneDeep(currentCase);

        // For all the fields that are removed in the current iteration, set value from Original case details
        previousFieldsSelected.forEach((prevField) => {
            const isFieldInCurrent = currentFieldsSelected.some(
                (x) =>
                    x.guid === prevField.guid ||
                    x.value.toUpperCase() === prevField.value.toUpperCase()
            );

            if (!isFieldInCurrent) {
                refreshCaseFieldValue(currentCaseCopy, prevField);
            }
        });

        const easyUpdateFieldsSelectedUpdated = itemDeleted
            ? currentFieldsSelected
            : getUpdatedEasyUpdateFields(currentCaseCopy, currentFieldsSelected);

        // For UI Reasons, always make case Notes the last item in the list
        const notesSectionId = CaseFileStaticItems.NotesSection.Id;

        if (easyUpdateFieldsSelectedUpdated.some((x) => x.id === notesSectionId)) {
            const index = easyUpdateFieldsSelectedUpdated.findIndex((x) => x.id === notesSectionId);
            easyUpdateFieldsSelectedUpdated.push(
                easyUpdateFieldsSelectedUpdated.splice(index, 1)[0]
            );
        }
        setEasyUpdateFieldsSelected(easyUpdateFieldsSelectedUpdated);
        setCurrentCase(currentCaseCopy);
    };

    const handleDeleteFieldClick = (item: IMultiSelectOption, force?: boolean) => {
        const flatAdGroups = ADGroups.flatMap((group) => group);

        if (flatAdGroups.includes(item.guid?.toUpperCase() ?? '') && !force) {
            setAdGroupFieldToDelete(item);
        } else {
            deleteFields([item]);
        }
    };

    const handleInputError = (item: IMultiSelectOption, error: string) => {
        item.externalValidationError = error;
        item.validationError = error;
    };

    const handlePreventRemoveValue = (preventedItem: IMultiSelectOption) => {
        if (!preventedItem.readOnly || preventedItem.preventRemoval) {
            handleDeleteFieldClick(preventedItem);
        }
    };

    const handleAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.CaseNote },
            status: { id: NoteStatusTypes.Open },
            caseGuid: currentCase.guid,
            purpose: { id: NotePurposeTypesEnum.Info },
        };

        setOpenNoteEditor(true);
        setCurrentNote(currentNote);
        setHasChanges(true);
    };

    const handleExpertsChanges = (caseExperts: ICaseExpertModel[]) => {
        const caseCopy = cloneDeep(currentCase);
        caseCopy.caseExperts = caseExperts;
        setCurrentCase(caseCopy);
        setHasChanges(true);
    };

    const handleSearchableSingleSelectChange = (
        optionsList: any,
        assertionDescriptorGuid?: string,
        staticItemId?: number,
        isBoolean?: boolean
    ) => {
        let currentCaseCopy = cloneDeep(currentCase);
        const selectedItem: IMultiSelectOption = cloneDeep(optionsList);

        if (assertionDescriptorGuid) {
            currentCaseCopy =
                CaseFileCheckmateSelectHelper.updateCaseOnSelectedAssertionDescriptorValueChange(
                    currentCaseCopy,
                    assertionDescriptorGuid,
                    isBoolean || false,
                    selectedItem,
                    assertionDescriptors
                );
        } else {
            switch (staticItemId) {
                case CaseFileStaticItems.CaseStatus.Id:
                    if (selectedItem && selectedItem.id) {
                        const match = caseStatuses.find((x) => x.id == selectedItem.id);
                        currentCaseCopy.caseStatus = JSON.parse(JSON.stringify(match));
                    } else currentCaseCopy.caseStatus = undefined;
                    break;
                case CaseFileStaticItems.CaseManager.Id:
                    if (selectedItem && selectedItem.guid) {
                        const match = zoneUsers.find((x) => x.guid == selectedItem.guid);
                        currentCaseCopy.caseManagerUser = cloneDeep(match);
                    } else currentCaseCopy.caseManagerUser = undefined;
                    break;
                case CaseFileStaticItems.AlternateCaseManager.Id:
                    if (selectedItem && selectedItem.guid) {
                        const match = zoneUsers.find((x) => x.guid == selectedItem.guid);
                        currentCaseCopy.alternateCaseManagerUser = cloneDeep(match);
                    } else currentCaseCopy.alternateCaseManagerUser = undefined;
                    break;
                case CaseFileStaticItems.LocalCounsel.Id:
                    // If first ever time adding any Defense Counsel; just add
                    if (!currentCaseCopy.defenseCounsels) {
                        if (selectedItem && selectedItem.guid)
                            currentCaseCopy.defenseCounsels = [
                                { guid: selectedItem.guid, priority: 1 },
                            ];
                        break;
                    }
                    // If no Local Counsel, Add
                    if (
                        currentCaseCopy.defenseCounsels.filter((x) => x.priority == 1).length === 0
                    ) {
                        if (selectedItem.guid)
                            currentCaseCopy.defenseCounsels.push({
                                guid: selectedItem.guid,
                                priority: 1,
                            });
                        break;
                    }
                    // Update previous value of Local Counsel either to a different value or remove all together
                    currentCaseCopy.defenseCounsels.filter((x) => x.priority == 1)[0].guid =
                        selectedItem && selectedItem.guid ? selectedItem.guid : undefined; // If no selectedItem, that means the Local Counsel is being removed. Hence set guid = undefined
                    break;
                case CaseFileStaticItems.CasePriority.Id:
                    currentCaseCopy.priority = selectedItem && selectedItem.id === 1;
                    break;
                default:
                    break;
            }
        }

        const updatedEasyUpdateFieldsSelected = getUpdatedEasyUpdateFields(
            currentCaseCopy,
            easyUpdateFieldsSelected
        );

        CaseDataCustomLogicHelper.Run(currentCaseCopy, assertionDescriptors);

        setCurrentCase(currentCaseCopy);
        setEasyUpdateFieldsSelected(updatedEasyUpdateFieldsSelected);
        setValidation({});
    };

    const handleMultiSelectValuesChanged = (optionsList: any, ad: IAssertionDescriptorModel) => {
        const currentCaseCopy = cloneDeep(currentCase);

        if (currentCaseCopy) {
            if (!currentCaseCopy.caseAssertions) currentCaseCopy.caseAssertions = [];

            currentCaseCopy.caseAssertions = currentCaseCopy.caseAssertions.filter(
                (x) => x.assertionDescriptorGuid !== ad!.guid
            );

            if (optionsList.length === 0) {
                currentCaseCopy.caseAssertions.push({
                    assertionDescriptorGuid: ad.guid,
                });
            } else {
                for (let i = 0; i < optionsList.length; i++) {
                    currentCaseCopy.caseAssertions.push({
                        assertionDescriptorGuid: ad.guid,
                        assertionDescriptorValueGuid: ad!.assertionDescriptorValues!.find(
                            (x) => x.guid === optionsList[i].guid!
                        )?.guid,
                    });
                }
            }

            setCurrentCase(currentCaseCopy);
        }
    };

    const handleViewEditNote = (noteGuidToEdit: string) => {
        const currentNote = currentCase.notes?.find((x) => x.guid == noteGuidToEdit);

        if (currentNote) {
            setCurrentNote(currentNote);
            setOpenNoteEditor(true);
        }
    };

    const handleNotesChanges = (notes: INoteModel[]) => {
        const caseItem = cloneDeep({ ...currentCase, notes });

        setCurrentCase(caseItem);
        setHasChanges(true);
    };

    const handleNarrativeFieldSelected = (field: IMultiSelectOption) => {
        const foundCA = currentCase.caseAssertions?.find(
            (ca) => ca.assertionDescriptorGuid === field.guid
        );
        let narrativeCA: ICaseAssertionModel | null = null;

        if (foundCA) {
            narrativeCA = cloneDeep(foundCA);
        } else {
            narrativeCA = { assertionDescriptorGuid: field.guid! };
        }

        setCurrentNarrativeCaseAssertion(narrativeCA);
    };

    const handleCancelNarrativeFieldEdit = () => {
        setCurrentNarrativeCaseAssertion(undefined);
    };

    const handleSaveNarrativeFieldToCase = (value: string) => {
        if (!currentNarrativeCaseAssertion) return;

        currentNarrativeCaseAssertion.textValue = value;
        const currentCaseCopy = cloneDeep(currentCase);

        const adMatch = assertionDescriptors.find(
            (x) => x.guid === currentNarrativeCaseAssertion.assertionDescriptorGuid
        );

        const assertionMatch = currentCaseCopy.caseAssertions?.find(
            (x) =>
                x.assertionDescriptorGuid === currentNarrativeCaseAssertion.assertionDescriptorGuid
        );

        if (!adMatch) return;

        if (assertionMatch) {
            CaseHelper.setCaseAssertionValue(assertionMatch, adMatch, value);
        } else {
            const newCaseAssertion: ICaseAssertionModel = {
                assertionDescriptorGuid: currentNarrativeCaseAssertion.assertionDescriptorGuid,
            };
            CaseHelper.setCaseAssertionValue(newCaseAssertion, adMatch!, value);
            currentCaseCopy.caseAssertions = currentCaseCopy.caseAssertions || [];
            currentCaseCopy.caseAssertions.push(newCaseAssertion);
        }

        setCurrentCase(currentCaseCopy);
        setCurrentNarrativeCaseAssertion(undefined);
    };

    const handleCaseSelected = (caseGuid: string) => {
        if (caseGuid === currentCase.guid) {
            setPendingResponse(true);
            caseDetailsApi.refetch().then(() => setPendingResponse(false));
        } else {
            setSelectedCaseGuid(caseGuid);
        }
    };

    const handleDepositionsChange = (updatedDepositions: IDepositionModel[]) => {
        const currentCaseCopy = cloneDeep(currentCase);
        currentCaseCopy.depositions = updatedDepositions;
        setCurrentCase(currentCaseCopy);
    };

    const handleDocumentSaveComplete = (documents: IDocumentModel[]) => {
        const caseCopy = cloneDeep(currentCase);
        const docsCopy = [...(caseCopy.documents ?? [])];

        for (const newDoc of documents) {
            const foundIdx = docsCopy.findIndex((doc) => doc.guid === newDoc.guid) ?? -1;
            if (foundIdx > -1) {
                docsCopy[foundIdx] = newDoc;
            } else {
                docsCopy.push(newDoc);
            }
        }
        docsCopy.sort(Sort.compareDate('modifiedDate', undefined, 'desc'));
        caseCopy.documents = docsCopy;

        setOriginalCase(caseCopy);
        setCurrentCase(caseCopy);
    };

    const handleDocumentDeleteComplete = (documentGuid: string) => {
        const caseCopy = cloneDeep(currentCase);
        caseCopy.documents = caseCopy.documents?.filter((doc) => doc.guid !== documentGuid);
        setOriginalCase(caseCopy);
        setCurrentCase(caseCopy);
    };

    useEffect(() => {
        props.onSelectedFieldsChange?.(easyUpdateFieldsSelected);
    }, [easyUpdateFieldsSelected]);

    // effects
    useEffect(() => {
        resetAllState();
    }, [refreshIdentifier]);

    useEffect(() => {
        if (props.caseGuid) {
            setSelectedCaseGuid(props.caseGuid);
        } else {
            setOriginalCase({ caseAssertions: [] } as ICaseModel);
            setCurrentCase({ caseAssertions: [] } as ICaseModel);
        }
    }, [props.caseGuid]);

    useEffect(() => {
        if (selectedCaseGuid && !props.hidden) {
            setPendingResponse(true);
            caseDetailsApi.refetch().then(() => setPendingResponse(false));
        }
    }, [selectedCaseGuid, props.hidden]);

    useEffect(() => {
        const caseFieldsAllCopy = cloneDeep(caseFieldsAll);
        const easyUpdateFieldsSelectedCopy = cloneDeep(easyUpdateFieldsSelected);

        const caseFieldsNotesSectionIndex = caseFieldsAllCopy.findIndex(
            (x) => x.id === CaseFileStaticItems.NotesSection.Id
        );

        const easyUpdateFieldsHasNotesSectionIndex = easyUpdateFieldsSelectedCopy.findIndex(
            (x) => x.id === CaseFileStaticItems.NotesSection.Id
        );

        if (props.excludeNotes) {
            if (caseFieldsNotesSectionIndex > -1) {
                caseFieldsAllCopy.splice(caseFieldsNotesSectionIndex, 1);
            }

            if (easyUpdateFieldsHasNotesSectionIndex > -1) {
                easyUpdateFieldsSelectedCopy.splice(easyUpdateFieldsHasNotesSectionIndex, 1);
            }
        } else if (caseFieldsNotesSectionIndex === -1) {
            const caseNotesField = {
                id: CaseFileStaticItems.NotesSection.Id,
                label: CaseHelper.getCaseFileStaticItemDisplayName(
                    staticItems,
                    CaseFileStaticItems.NotesSection.Id
                ),
                value: UIHelper.getName(staticItems, CaseFileStaticItems.NotesSection.Id),
            };

            caseFieldsAllCopy.push(caseNotesField);
        }

        setCaseFieldsAll(caseFieldsAllCopy);
        setEasyUpdateFieldsSelected(easyUpdateFieldsSelectedCopy);
    }, [props.excludeNotes]);

    useEffect(() => {
        if (props.hidden) {
            setValidation({});
        }
    }, [props.hidden]);

    useEffect(() => {
        if (staticItemsApi.isFetched && assertionDescriptorsApi.isFetched) {
            const queryCriteriaList = getCriteriaList();
            setCaseFieldsAll(queryCriteriaList);
        }
    }, [staticItemsApi.isFetched, assertionDescriptorsApi.isFetched]);

    useEffect(() => {
        if (
            assertionDescriptorsApi.isFetched &&
            staticItemsApi.isFetched &&
            !caseDetailsApi.isFetching &&
            caseDetailsApi.isFetched &&
            caseDetailsApi.data
        ) {
            const caseCopy = cloneDeep(caseDetailsApi.data);

            CaseDataCustomLogicHelper.Run(caseCopy, assertionDescriptors);

            // CHECK-1732 - remove the fields that were previously system generated
            const filteredSelectedFields = easyUpdateFieldsSelected.filter(
                (field) => !systemGeneratedIds.includes(field.guid ?? '')
            );

            const updatedEasyUpdateFieldsSelected = getUpdatedEasyUpdateFields(
                caseCopy,
                filteredSelectedFields
            );

            setCurrentCase(caseCopy);
            setOriginalCase(caseCopy);
            setEasyUpdateFieldsSelected(updatedEasyUpdateFieldsSelected);
            setPendingResponse(false);
        }
    }, [
        assertionDescriptorsApi.isFetched,
        staticItemsApi.isFetched,
        caseDetailsApi.isFetching,
        caseDetailsApi.isFetched,
        caseDetailsApi.data,
    ]);

    useEffect(() => {
        if (caseDetailsApi.isError && caseDetailsApi.error) {
            setPendingResponse(false);
            const errorMessage =
                (caseDetailsApi.error as Error)?.message || DisplayMessages.UnexpectedError;
            showErrorToast(errorMessage);
        }
    }, [caseDetailsApi.isError, caseDetailsApi.error]);

    useEffect(() => {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.CaseEasyUpdate,
                user,
                zoneContext.zoneGuid
            )
        ) {
            window.location.assign(zoneContext.buildZoneUrl(LocalRoutes.AccessDenied));
        }
    }, []);

    const lookupsAreLoading =
        zoneUsersApi.isFetching ||
        defenseCounselsApi.isFetching ||
        assertionDescriptorsApi.isFetching ||
        staticItemsApi.isFetching ||
        noteCategoryTypesApi.isFetching ||
        organizationSettingsApi.isFetching ||
        caseMangersApi.isFetching ||
        caseStatusTypesApi.isFetching ||
        defaultOrgApi.isFetching ||
        caseDetailsApi.isFetching;

    const availableFields = getAllEasyUpdateFieldsForMultiSelect();

    const getNarrativeModalTitle = () => {
        const foundAD = assertionDescriptors.find(
            (ad) => ad.guid === currentNarrativeCaseAssertion?.assertionDescriptorGuid
        );

        if (foundAD) {
            const { displayName, alternateDisplayName, name } = foundAD;

            return alternateDisplayName || displayName || name || '';
        }

        return '';
    };

    const getDifferences = () => {
        const {
            notes: origNotes,
            caseExperts: origExperts,
            depositions: origDepos,
            ...restOfOriginalCase
        } = originalCase;

        const { notes, caseExperts, depositions, ...restOfCurrentCase } = currentCase;

        const differences =
            UIHelper.difference(cloneDeep(restOfCurrentCase), cloneDeep(restOfOriginalCase)) ?? {};

        return differences;
    };

    const hasUnsavedChanges = addNewCase || !isEmpty(getDifferences());

    // render
    if (props.hidden) {
        return <div />;
    }

    if (currentCaseExpert) {
        return (
            <CaseExpertEditor
                caseExpertGuid={currentCaseExpert.guid}
                caseGuid={currentCase.guid}
                defaultIsEditMode={currentCaseExpert.defaultIsEditMode ?? false}
                onBackClick={() => setCurrentCaseExpert(null)}
                onSave={handleCaseExpertEditorSave}
                user={user}
            />
        );
    }

    return (
        <>
            <DocumentTitle title={props.documentTitle ?? 'Easy Update'} />
            {(pendingResponse || lookupsAreLoading) && <Loader />}
            {currentNote && openNoteEditor && (
                <MainContentOverlayComponent>
                    <NoteEditor
                        caseLocalCounselGuid={CaseHelper.getCaseLocalCounselGuid(currentCase)}
                        defaultAlertTarget={CaseHelper.getDefaultAlertTargetForCase(
                            user,
                            currentCase,
                            zoneContext.zoneGuid
                        )}
                        defaultWatchTargets={CaseHelper.getDefaultWatchTargetsForCase(
                            user,
                            currentCase,
                            zoneContext.zoneGuid
                        )}
                        caseTitle={CaseHelper.getCaseTitle(currentCase)}
                        noteCategoryTypes={noteCategoryTypes}
                        currentNote={currentNote}
                        onSaveComplete={handleSaveNoteComplete}
                        onCancel={handleCancelNoteEditor}
                        useNoteAssociationsModal
                        noteAssociationTitle="Add Related Case Expert(s)"
                        noteAssociationEntityType={{ id: EntityTypes.CaseExpert }}
                        noteAssociationEntityItems={currentCase.caseExperts?.map(
                            (ce) =>
                                ({
                                    label: ce.name,
                                    value: ce.guid,
                                    guid: ce.guid,
                                }) as IMultiSelectOption
                        )}
                    />
                </MainContentOverlayComponent>
            )}
            <PageWithAffixedHeader
                headerComponent={
                    <CaseEasyUpdateHeader
                        additionalHeaderText={props.additionalHeaderText}
                        addNewCase={addNewCase}
                        changesDetected={hasUnsavedChanges}
                        currentCase={currentCase}
                        currentFavoriteDetailsString={currentFavoriteDetailsString}
                        easyUpdateFieldsSelected={easyUpdateFieldsSelected}
                        onAddNewCase={handleAddNewCase}
                        onApplyFavoriteSelected={handleApplyFavoriteSelected}
                        onBack={handleBackClick}
                        onCancel={props.onCancel}
                        onClear={handleClearAll}
                        onFavoriteSelected={handleFavoriteSelected}
                        onSaveCase={handleSaveCase}
                    />
                }
            >
                {validation.model && (
                    <div className="text-danger">
                        {validation.model.map((value: string, idx: number) => {
                            return (
                                <ul key={idx}>
                                    {value.split('|').map((s: string, idx: number) => {
                                        return <li key={idx}>{s}</li>;
                                    })}
                                </ul>
                            );
                        })}
                    </div>
                )}

                <div id="queryDetails" className="margin-top panel-body">
                    {addNewCase || props.caseGuid ? null : (
                        <CaseSearchControl
                            onCaseSelected={handleCaseSelected}
                            onClearSearchFilter={handleClearSearchControl}
                        />
                    )}

                    <div className="row">
                        <div className="col-sm-2">
                            <label className="text-gray text-lg">Fields</label>
                        </div>
                        <div className="col-sm-10">
                            <CheckmateSelect
                                isMulti
                                options={availableFields}
                                value={easyUpdateFieldsSelected}
                                onChange={handleEasyUpdateFieldsSelected}
                                onPreventRemoveValue={handlePreventRemoveValue}
                            />
                        </div>
                        <span className="text-danger">{validation.criteria}</span>
                    </div>

                    {easyUpdateFieldsSelected?.length > 0 && (
                        <div>
                            <table className="table margin-top-sm">
                                <thead>
                                    <tr>
                                        <th className="col-sm-3">Field</th>
                                        <th className="col-sm-4">Value(s)</th>
                                        <th />
                                    </tr>
                                </thead>
                                <tbody>
                                    {easyUpdateFieldsSelected.map((item: IMultiSelectOption) => {
                                        const rowKey = item.value + item.id + item.guid;

                                        const isSameCaseGroupPendingLockedField =
                                            (lockableAssertionDescriptors.includes(
                                                item.guid?.toUpperCase() || ''
                                            ) ||
                                                item.id === CaseFileStaticItems.CaseNumber.Id) &&
                                            !!isPendingCaseInSameCaseGroup;

                                        const isSameCaseGroupApprovedLockedField =
                                            (lockableAssertionDescriptors.includes(
                                                item.guid?.toUpperCase() || ''
                                            ) ||
                                                item.id === CaseFileStaticItems.CaseNumber.Id) &&
                                            !!isApprovedCaseInSameCaseGroup;

                                        item.readOnly =
                                            item.readOnly ||
                                            isSameCaseGroupPendingLockedField ||
                                            isSameCaseGroupApprovedLockedField;

                                        return (
                                            <CaseEasyUpdateFieldsTableRow
                                                assertionDescriptors={assertionDescriptors}
                                                caseStatuses={caseStatuses}
                                                currentCase={currentCase}
                                                defenseCounsels={defenseCounsels}
                                                getValue={getValue}
                                                item={item}
                                                isSameCaseGroupPendingLockedField={
                                                    isSameCaseGroupPendingLockedField
                                                }
                                                isSameCaseGroupApprovedLockedField={
                                                    isSameCaseGroupApprovedLockedField
                                                }
                                                key={rowKey}
                                                noteCategoryTypes={noteCategoryTypes}
                                                onAddNote={handleAddNote}
                                                onChange={handleChange}
                                                onDepositionsChange={handleDepositionsChange}
                                                onDeleteFieldClick={handleDeleteFieldClick}
                                                onInputError={handleInputError}
                                                onDocumentDeleteComplete={handleDocumentDeleteComplete}
                                                onDocumentSaveComplete={handleDocumentSaveComplete}
                                                onExpertsChanges={handleExpertsChanges}
                                                onMultiSelectValuesChanged={
                                                    handleMultiSelectValuesChanged
                                                }
                                                onNarrativeFieldEdit={handleNarrativeFieldSelected}
                                                onSearchableSingleSelectChange={
                                                    handleSearchableSingleSelectChange
                                                }
                                                onSetCurrentCaseExpert={setCurrentCaseExpert}
                                                onNotesChanges={handleNotesChanges}
                                                onViewEditNote={handleViewEditNote}
                                                zoneUsers={zoneUsers}
                                            />
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </PageWithAffixedHeader>

            {currentNarrativeCaseAssertion && (
                <CaseNarrativeModal
                    title={getNarrativeModalTitle()}
                    assertion={currentNarrativeCaseAssertion}
                    validation={validation}
                    onSubmit={handleSaveNarrativeFieldToCase}
                    onCancel={handleCancelNarrativeFieldEdit}
                />
            )}

            <CheckmateDialog
                isShowingModal={!!adGroupFieldToDelete}
                title="Delete Group"
                body="This field is part of a group. Deleting it will also delete the rest of the group. Are you sure you wish to delete it?"
                handleClose={() => setAdGroupFieldToDelete(undefined)}
                cancelText="Cancel"
                closeButtonClassName="btn btn-default"
                confirmButtonClassName="btn btn-black"
                confirmText="Delete"
                handleConfirm={() => {
                    handleDeleteFieldClick(adGroupFieldToDelete!, true);
                    setAdGroupFieldToDelete(undefined);
                }}
                showBackdrop
            />
        </>
    );
}

export default CaseEasyUpdate;
